import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getPlans } from "../../apis/plans";
import { createPayment } from "../../apis/payments";
import { trackCtaClicked } from "../../analytics/analytics";
import { subscribeCtaId, ANALYTICS_CTAS } from "../../analytics/analytics.constants";
import { APP_ROUTES } from "../../constants/appRoutes";
import { fetchCurrentPlan } from "../../store/planSlice";
import { isSubscribedPlan } from "../../utils/normalizePlan";
import FullWidthLayout from "../shared/FullWidthLayout";
import PlanCardSkeletons from "./PlanCardSkeletons";

const SUPPORT_EMAIL = "support@EzePulse.com";

const COMMON_FEATURES = [
    { icon: "envelope", label: "Email support", },
    { icon: "lightbulb-fill", label: "AI Marketing Trends",  },
];

function formatDuration(days) {
    const n = Number(days);
    if (n === 1) return "1 day";
    return `${n} days`;
}

function formatPlanPrice(price) {
    if (!price || price.unit_amount == null) return null;

    const currency = String(price.currency || "inr").toUpperCase();
    const amount = Number(price.unit_amount) / 100;

    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `₹${amount}`;
    }
}

function formatBillingSuffix(price) {
    if (!price) return null;

    if (price.type === "one_time" || !price.recurring) {
        return "(Only Once)";
    }

    const interval = price.recurring.interval;
    const labels = {
        month: "/ Month",
        week: "/ Week",
        day: "/ Day",
        year: "/ Year",
    };

    return labels[interval] ?? `/ ${interval}`;
}

function isRecurringPlan(plan) {
    const price = plan.price;
    if (!price) return false;
    return price.type !== "one_time" && Boolean(price.recurring);
}

function isOneTimePlan(plan) {
    const price = plan.price;
    if (!price) return true;
    return price.type === "one_time" || !price.recurring;
}

function BillingToggle({ value, onChange, disabled = false }) {
    return (
        <div className="mb-8 flex justify-center">
            <div
                className="inline-flex rounded-full border border-slate-200 bg-slate-100 p-1"
                role="group"
                aria-label="Billing type"
            >
                <button
                    type="button"
                    disabled={disabled}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                        value === "one_time"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                    } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                    aria-pressed={value === "one_time"}
                    onClick={() => onChange("one_time")}
                >
                    Pay once
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                        value === "recurring"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                    } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                    aria-pressed={value === "recurring"}
                    onClick={() => onChange("recurring")}
                >
                    Recurring
                </button>
            </div>
        </div>
    );
}

function PlanCard({ plan, isCurrent, onSubscribe, isCheckoutLoading, isActiveCheckout }) {
    const isSubscribing = isCheckoutLoading && isActiveCheckout;
    const isHighlighted = plan.plan_id === "monthly_lite";
    const formattedPrice = formatPlanPrice(plan.price);
    const billingSuffix = formatBillingSuffix(plan.price);
    const description = plan.description?.trim() || "";

    return (
       
        <article
            className={`mx-auto flex h-full w-full max-w-[27rem] min-h-[520px] flex-col rounded-2xl border bg-white p-6 shadow-sm transition ${
                isHighlighted ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"
            } ${isCurrent ? "ring-2 ring-emerald-500/30" : ""}`}
        >
            <div className="mb-8 min-h-[7.5rem]">
                <div className="mb-2 flex min-h-[1.5rem] items-start justify-end gap-2">
                    {isHighlighted ? (
                        <span className="shrink-0 rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                            Best value
                        </span>
                    ) : null}
                    {isCurrent ? (
                        <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                            Current
                        </span>
                    ) : null}
                </div>
                <h3 className="mb-1 text-3xl font-bold text-slate-900">{plan.name}</h3>
                <p
                    className={`mb-0 min-h-[2.75rem] text-sm leading-relaxed text-slate-600 ${
                        description ? "" : "invisible"
                    }`}
                >
                    {description || "—"}
                </p>
            </div>

            <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                    {formattedPrice ? (
                        <p className="mb-0 text-3xl font-bold tracking-tight text-slate-900">
                            {formattedPrice}
                        </p>
                    ) : (
                        <p className="mb-0 text-3xl font-bold tracking-tight text-slate-900">Free</p>
                    )}
                    {billingSuffix ? (
                        <span className="text-sm font-medium text-slate-500">{billingSuffix}</span>
                    ) : null}
                </div>
                <p className="mb-0 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Quota period
                </p>
                <p className="mb-0 text-sm font-semibold text-slate-800">
                    {formatDuration(plan.duration_days)}
                </p>
            </div>

            <ul className="mb-6 flex flex-1 flex-col space-y-6 ps-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                    <i className="bi bi-megaphone text-slate-400" aria-hidden />
                    <span>
                        <strong className="font-semibold text-slate-900">{plan.campaign_quota}</strong>{" "}
                        {plan.campaign_quota === 1 ? "campaign" : "campaigns"}
                    </span>
                </li>
                <li className="flex items-center gap-2">
                    <i className="bi bi-file-earmark-text text-slate-400" aria-hidden />
                    <span>
                        <strong className="font-semibold text-slate-900">{plan.post_quota}</strong>{" "}
                        {plan.post_quota === 1 ? "post" : "posts"}
                    </span>
                </li>
                <li className="flex items-center gap-2">
                    <i className="bi bi-calendar3 text-slate-400" aria-hidden />
                    <span>
                        Valid for{" "}
                        <strong className="font-semibold text-slate-900">
                            {formatDuration(plan.duration_days)}
                        </strong>
                    </span>
                </li>
                {COMMON_FEATURES.map((feature) => (
                    <li key={feature.label} className="flex items-center gap-2">
                        <i className={`bi bi-${feature.icon} text-slate-400 ${feature.iconClass}`} aria-hidden />
                        <span>{feature.label}</span>
                    </li>
                ))}
            </ul>

            <button
                type="button"
                className={`btn mt-auto w-full px-4 py-2.5 text-sm font-semibold ${
                    isCurrent
                        ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-500"
                        : "btn-dark-app"
                }`}
                disabled={isCurrent || isCheckoutLoading || plan.is_active === false}
                onClick={() => {
                    if (isCurrent) return;
                    onSubscribe(plan);
                }}
            >
                {isCurrent ? (
                    "Current plan"
                ) : isSubscribing ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                        Redirecting…
                    </>
                ) : (
                    <>
                        Subscribe
                        <i className="bi bi-arrow-right ms-2" aria-hidden />
                    </>
                )}
            </button>
        </article>
    );
}

function PlansComingSoon({ isLoggedIn }) {
    return (
        <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm md:px-10 md:py-14">
            <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                <i className="bi bi-hourglass-split text-2xl" aria-hidden />
            </span>
            <h2 className="mb-3 text-xl font-bold text-slate-900 md:text-2xl">Paid plans are coming soon</h2>
            <p className="mb-0 text-sm leading-relaxed text-slate-600 md:text-base">
                Please reach out to{" "}
                <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                >
                    {SUPPORT_EMAIL}
                </a>{" "}
                if you need early access.
            </p>

            {isLoggedIn ? (
                <Link
                    to={APP_ROUTES.HOME}
                    onClick={() => trackCtaClicked(ANALYTICS_CTAS.NAV_HOME)}
                    className="btn btn-dark-app mt-8 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold no-underline"
                >
                    <i className="bi bi-grid-fill" aria-hidden />
                    Go to dashboard
                </Link>
            ) : (
                <div className="mt-8 space-y-3">
                    <p className="mb-0 text-sm font-medium text-slate-700">
                        Get a free 7 days access
                    </p>
                    <Link
                        to={APP_ROUTES.HOME}
                        onClick={() => trackCtaClicked(ANALYTICS_CTAS.GET_STARTED)}
                        className="btn btn-dark-app inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold no-underline"
                    >
                        Get started
                        <i className="bi bi-arrow-right" aria-hidden />
                    </Link>
                </div>
            )}
        </div>
    );
}

export default function Plans() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const userId = user?.uid;
    const { data: currentPlanData, loaded: planLoaded } = useSelector((state) => state.plan);

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [activeCheckoutPlanId, setActiveCheckoutPlanId] = useState(null);
    const [billingMode, setBillingMode] = useState("recurring");

    useEffect(() => {
        if (!planLoaded) {
            dispatch(fetchCurrentPlan());
        }
    }, [dispatch, planLoaded]);

    const filteredPlans = plans.filter((plan) =>
        billingMode === "recurring" ? isRecurringPlan(plan) : isOneTimePlan(plan)
    );
    const showBillingToggle = !loading && !error && plans.length > 0;

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await getPlans();
                const list = (data?.plans ?? [])
                    .filter((p) => p.is_active !== false)
                    .sort((a, b) => a.duration_days - b.duration_days);

                if (!cancelled) setPlans(list);
            } catch (err) {
                if (!cancelled) {
                    const message =
                        typeof err === "string"
                            ? err
                            : err?.response?.data?.message || err?.message || "Failed to load plans";
                    setError(message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleSubscribe = useCallback(
        async (plan) => {
            if (!plan.plan_id || isSubscribedPlan(currentPlanData, plan.plan_id)) return;

            trackCtaClicked(subscribeCtaId(plan.name));

            setIsCheckoutLoading(true);
            setActiveCheckoutPlanId(plan.plan_id);

            try {
                const data = await createPayment({ plan_id: plan.plan_id });
                const checkoutUrl = data?.checkout_url ?? data?.checkoutUrl;

                if (!checkoutUrl) {
                    throw new Error("Checkout URL was not returned");
                }

                window.location.assign(checkoutUrl);
            } catch (err) {
                const message =
                    typeof err === "string"
                        ? err
                        : err?.response?.data?.message || err?.message || "Could not start checkout";
                toast.error(message);
                setIsCheckoutLoading(false);
                setActiveCheckoutPlanId(null);
            }
        },
        [userId, currentPlanData]
    );

    return (
        <FullWidthLayout>
            

            {error ? (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            ) : null}

            {loading ? <PlanCardSkeletons count={3} /> : null}

            {!loading && showBillingToggle ? (
                <BillingToggle
                    value={billingMode}
                    onChange={setBillingMode}
                    disabled={isCheckoutLoading}
                />
            ) : null}

            {!loading && !error && plans.length === 0 ? (
                <PlansComingSoon isLoggedIn={Boolean(user)} />
            ) : null}

            {!loading && !error && plans.length > 0 && filteredPlans.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
                    No {billingMode === "recurring" ? "recurring" : "one-time"} plans available right now.
                </div>
            ) : null}

            {!loading && filteredPlans.length > 0 ? (
            <div className="grid auto-rows-fr items-stretch gap-5 md:grid-cols-2 xl:grid-cols-3">
                {filteredPlans.map((plan) => (
                    <PlanCard
                        key={plan.plan_id}
                        plan={plan}
                        isCurrent={isSubscribedPlan(currentPlanData, plan.plan_id)}
                        onSubscribe={handleSubscribe}
                        isCheckoutLoading={isCheckoutLoading}
                        isActiveCheckout={activeCheckoutPlanId === plan.plan_id}
                    />
                ))}
            </div>
            ) : null}
        </FullWidthLayout>
    );
}
